import React, { useState, useEffect, useCallback } from 'react';
import { analyticsAPI, locationsAPI, taxonomyAPI, usersAPI } from '../utils/api';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line
} from 'recharts';
import { 
  TrendingUp, CheckCircle, AlertCircle, Package, Calendar, MapPin, 
  Filter, RefreshCw, Layers, User, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';

const COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
const HEALTH_COLORS = { 
  operational: '#10B981', 
  under_maintenance: '#F59E0B', 
  faulty: '#EF4444', 
  new: '#0EA5E9', 
  decommissioned: '#64748B' 
};

function KPICard({ title, value, sub, icon: Icon, color }) {
  return (
    <div className="card" style={{ padding: '24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ 
            width: 44, height: 44, borderRadius: 12, background: `${color}15`, 
            display: 'flex', alignItems: 'center', justifyContent: 'center' 
          }}>
            <Icon size={22} color={color} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {title}
          </div>
        </div>
        <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>
          {value || 0}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {sub}
        </div>
      </div>
      <div style={{ 
        position: 'absolute', top: -10, right: -10, fontSize: 80, 
        color: `${color}08`, opacity: 0.2, fontWeight: 900, pointerEvents: 'none'
      }}>
        <Icon size={120} />
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters state
  const [filters, setFilters] = useState({
    startDate: '', // Default could be 30 days ago
    endDate: '',
    siteId: '',
    categoryId: '',
    assignedTo: ''
  });

  // Master data for filters
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const loadFilters = async () => {
    try {
      const [sr, cr, tr] = await Promise.all([
        locationsAPI.sites(),
        taxonomyAPI.categories(),
        usersAPI.technicians()
      ]);
      setSites(sr.data.sites);
      setCategories(cr.data.categories);
      setTechnicians(tr.data.users);
    } catch (e) { console.error('Failed to load filters', e); }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.get(filters);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const complianceRate = data?.summary 
    ? Math.round((parseInt(data.summary.completed_jobs) / parseInt(data.summary.total_jobs || 1)) * 100) 
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Filter Bar */}
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--accent)' }}>
            <Filter size={18} />
            <span style={{ fontWeight: 700, fontSize: 14 }}>Global Filters</span>
          </div>
          
          <div style={{ height: 24, width: 1, background: 'var(--border)' }} />

          <div style={{ display: 'flex', gap: 12, flex: 1, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
              <select className="form-select" value={filters.siteId} 
                onChange={e => setFilters({...filters, siteId: e.target.value})}>
                <option value="">All Sites</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            
            <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
              <select className="form-select" value={filters.categoryId} 
                onChange={e => setFilters({...filters, categoryId: e.target.value})}>
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
              <select className="form-select" value={filters.assignedTo} 
                onChange={e => setFilters({...filters, assignedTo: e.target.value})}>
                <option value="">All Technicians</option>
                {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="date" className="form-input" style={{ width: 140 }} 
                value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} />
              <span style={{ color: 'var(--text-muted)' }}>to</span>
              <input type="date" className="form-input" style={{ width: 140 }} 
                value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} />
            </div>
          </div>

          <button className="btn-icon" onClick={loadData} title="Refresh Data">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          {[1,2,3,4].map(i => (
            <div key={i} className="card" style={{ height: 140, background: 'var(--bg-muted)', opacity: 0.5 }} />
          ))}
        </div>
      ) : (
        <>
          {/* KPI Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            <KPICard 
              title="Asset Count" 
              value={data?.summary?.total_assets} 
              sub="Assets in current selection" 
              icon={Package} 
              color="#0EA5E9" 
            />
            <KPICard 
              title="Job Compliance" 
              value={`${complianceRate}%`} 
              sub="Completed vs Assigned" 
              icon={CheckCircle} 
              color="#10B981" 
            />
            <KPICard 
              title="Avg. Resolution" 
              value={`${Math.round(data?.summary?.avg_duration || 0)}m`} 
              sub="MTTR (Mean Time To Repair)" 
              icon={RefreshCw} 
              color="#F59E0B" 
            />
            <KPICard 
              title="Overdue Tasks" 
              value={data?.summary?.overdue_jobs} 
              sub="Immediate attention required" 
              icon={AlertCircle} 
              color="#EF4444" 
            />
          </div>

          {/* Charts Row 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24 }}>
            <div className="card" style={{ padding: 24 }}>
              <div className="card-header" style={{ padding: 0, marginBottom: 24 }}>
                <div className="card-title">Asset Health</div>
              </div>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data?.health || []}
                      cx="50%" cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="status"
                    >
                      {(data?.health || []).map((entry, idx) => (
                        <Cell key={idx} fill={HEALTH_COLORS[entry.status] || COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
                      itemStyle={{ fontSize: 12, fontWeight: 700 }}
                    />
                    <Legend iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card" style={{ padding: 24 }}>
              <div className="card-header" style={{ padding: 0, marginBottom: 24 }}>
                <div className="card-title">Maintenance Trends</div>
              </div>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data?.trends || []}>
                    <defs>
                      <linearGradient id="colorTrends" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <Tooltip 
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
                    />
                    <Area type="monotone" dataKey="count" stroke="#0EA5E9" strokeWidth={3} fillOpacity={1} fill="url(#colorTrends)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="card" style={{ padding: 24 }}>
            <div className="card-header" style={{ padding: 0, marginBottom: 24 }}>
              <div className="card-title">Category Performance Analysis</div>
            </div>
            <div style={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.categories || []} margin={{ bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="category" axisLine={false} tickLine={false} angle={-25} textAnchor="end" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <Tooltip 
                    cursor={{ fill: 'var(--bg-muted)' }}
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
                  />
                  <Legend verticalAlign="top" align="right" />
                  <Bar dataKey="job_count" name="Total Jobs" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed_count" name="Completed" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
